import { Permission } from "@/types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  ChipProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo } from "react";
import { useAppTheme } from "@/hooks/useAppTheme";

const ACTION_COLORS: Record<string, ChipProps["color"]> = {
  list: "info",
  show: "info",
  create: "success",
  update: "warning",
  delete: "error",
  search: "info",
  approve: "success",
  export: "default",
} as const;

const getActionColor = (action: string): ChipProps["color"] => ACTION_COLORS[action] ?? "default";

export const PermissionsDisplay = ({ permissions }: { permissions: Permission[] }) => {
  const { theme } = useAppTheme();

  const groupedPermissions = useMemo(() => {
    return permissions.reduce<Record<string, Permission[]>>((acc, perm) => {
      const key = perm.resource;
      if (!acc[key]) acc[key] = [];
      acc[key].push(perm);
      return acc;
    }, {});
  }, [permissions]);

  const resources = Object.keys(groupedPermissions).sort();

  if (permissions.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No permissions assigned to this role
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {permissions.length} permissions across {resources.length} resources
      </Typography>
      {resources.map((resource) => {
        const resourcePerms = groupedPermissions[resource];
        const actions = resourcePerms.map((p) => p.action).sort();

        return (
          <Accordion
            key={resource}
            disableGutters
            sx={{
              "&:before": { display: "none" },
              bgcolor: "transparent",
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
                mb: 0.5,
                minHeight: 40,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2" sx={{ textTransform: "capitalize", minWidth: 180 }}>
                  {resource.replace(/-/g, " ")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({actions.length} actions)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1, pb: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {actions.map((action) => (
                  <Tooltip key={action} title={`Can ${action} ${resource}`}>
                    <Chip
                      label={action}
                      size="small"
                      color={getActionColor(action)}
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
